import { useEffect, useRef, useState } from 'react'
import { getDate, getMonth, getYear, isEqual } from 'date-fns'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { DAY } from '../../../constants/date'
import { NUMBER_SIZE } from '../../../constants/size'
import { useDate, useTimeZone, useVariableRef } from '../../../hooks'
import { isEqualNullable } from '../../../utils/common'
import { createDay } from '../../../utils/date'
import { DateSelectOptions } from '../DateSelectOptions'
import { MonthInput as DefaultMonthInput } from '../MonthInput'
import { WeekDays as DefaultWeekDays } from '../WeekDays'

import * as styles from './DateSelect.module.css'

function DateSelect({
  Element = 'div',
  MonthInput = DefaultMonthInput,
  Options = DateSelectOptions,
  WeekDays = DefaultWeekDays,
  children,
  className = styles.DateSelect,
  createDate = createDay,
  disabled,
  maxValue,
  minValue,
  monthInput = true,
  monthInputProps: {
    borderless: monthInputBorderless = true,
    className: monthInputClassName = styles.monthInput,
    size: monthInputSize = NUMBER_SIZE.size24,
    ...monthInputProps
  } = EMPTY_OBJECT,
  optionsProps: {
    className: optionsClassName = styles.options,
    size: optionsSize = NUMBER_SIZE.size40,
    ...optionsProps
  } = EMPTY_OBJECT,
  value,
  weekDays = true,
  weekDaysProps: {
    className: weekDaysClassName = styles.weekDays,
    size: weekDaysSize = NUMBER_SIZE.size40,
    ...weekDaysProps
  } = EMPTY_OBJECT,
  onChange,
  ...elementProps
}) {
  const timeZone = useTimeZone()
  const timeZoneRef = useRef(timeZone)

  const valueRef = useVariableRef(value)
  const onChangeRef = useVariableRef(onChange)

  const today = useDate(DAY)

  const [monthValue, setMonthValue] = useState(value || today)

  useEffect(() => {
    if (timeZone !== timeZoneRef.current) {
      const { current: currValue } = valueRef

      const nextValue = currValue
        ? createDate(
            timeZone,
            getYear(currValue),
            getMonth(currValue),
            getDate(currValue)
          )
        : null

      timeZoneRef.current = timeZone

      if (!isEqualNullable(nextValue, currValue, isEqual)) {
        onChangeRef.current?.(nextValue)
      }
    }
  }, [createDate, onChangeRef, timeZone, valueRef])

  function handleOptionsSelect(date) {
    const nextValue = createDate(
      timeZone,
      getYear(date),
      getMonth(date),
      getDate(date)
    )

    if (!isEqualNullable(nextValue, valueRef.current, isEqual)) {
      onChangeRef.current?.(nextValue)
    }
  }

  const selected = value ? [value] : EMPTY_LIST

  return (
    <Element {...elementProps} className={className}>
      {monthInput === true ? (
        <MonthInput
          {...monthInputProps}
          borderless={monthInputBorderless}
          className={monthInputClassName}
          disabled={monthInputProps.disabled || disabled}
          maxValue={maxValue}
          minValue={minValue}
          size={monthInputSize}
          value={monthValue}
          onChange={setMonthValue}
        />
      ) : (
        weekDays
      )}
      {weekDays === true ? (
        <WeekDays
          {...weekDaysProps}
          className={weekDaysClassName}
          size={weekDaysSize}
        />
      ) : (
        weekDays
      )}
      <Options
        {...optionsProps}
        className={optionsClassName}
        disabled={disabled}
        maxValue={maxValue}
        minValue={minValue}
        selected={selected}
        size={optionsSize}
        today={today}
        value={monthValue}
        onSelect={handleOptionsSelect}
      />
      {children}
    </Element>
  )
}

export default DateSelect
